import { useCallback } from 'react';
import { datadogRum } from '@datadog/browser-rum';

/**
 * Platform usage instrumentation.
 *
 * Emits a single, consistently-shaped Datadog RUM custom action for any
 * feature in afy-ui so we can build cross-feature usage dashboards and map
 * which customers use which parts of the platform.
 *
 * Event name convention: `<feature>.<action>` where `feature` is kebab-case
 * and `action` is snake_case (e.g. `editor.save_cover`, `order.submit`).
 * Every event carries `feature`, `action` and `route` so dashboards can group
 * by feature without a widget per feature.
 *
 * RUM already attaches `@usr.email`, `@geo.country` and `@session.ip` to every
 * action (see `setUser` in configuration/datadog.js), which is what powers the
 * per-customer rollups and the member-vs-team (US/CA vs PH/DR/PK) split.
 *
 * IMPORTANT: never pass free text or PII in `props` — only ids, counts and
 * enum-like values. Like initializeUser(), this never throws into product code.
 */
export const trackUsage = (feature, action, props = {}) => {
  try {
    datadogRum.addAction(`${feature}.${action}`, {
      feature,
      action,
      route: window?.location?.pathname,
      ...props,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Error in trackUsage(${feature}.${action}): ${err}`);
  }
};

/**
 * Hook variant for component/button handlers (features that do not flow
 * through the legacy Redux-Saga action constants picked up by the tracking
 * middleware — e.g. GraphQL-mutation based flows).
 *
 *   const track = useTrackUsage();
 *   ...
 *   onCompleted: () => track('email-campaign', 'launch', { campaignId }),
 */
export const useTrackUsage = () =>
  useCallback((feature, action, props = {}) => trackUsage(feature, action, props), []);

export default trackUsage;
